import { useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../app/store";
import { roleInterface } from "../features/organization/organization.types";
import { AccessOptionForTaskDto } from "../features/tasks/types";
import {
  findElementByGroupId,
  findElementByGroupIdResponse,
} from "../helpers/treeHelpers";
import { MemberStatus, UserRole } from "../features/users/users.types";
import {
  FullProjectDto,
  ProjectAccessDto,
} from "../features/tasks/project/project.types";

function getRoleLevel(role: string): number {
  switch (role.toUpperCase()) {
    case "GUEST":
      return 0;
    case "VIEWER":
      return 1;
    case "EDITOR":
      return 2;
    case "ADMIN":
      return 3;
    case "OWNER":
      return 4;
    default:
      return -1; // Return -1 for unknown roles
  }
}

// Same as the backend (roles.guard.ts permissionTable): "admin" for managing
// members and deleting goals, "delete" (owner only) for deleting the
// group/organization itself
const permissionTable = {
  OWNER: ["read", "write", "admin", "delete"],
  ADMIN: ["read", "write", "admin"],
  EDITOR: ["read", "write"],
  VIEWER: ["read"],
  GUEST: ["read"],
  PRIVATE: [""],
};
export type permissionType = "read" | "write" | "admin" | "delete";

function hasRequiredPermission(
  permissionRole: UserRole,
  requiredPermission: string,
): boolean {
  return permissionTable[permissionRole].includes(requiredPermission);
}

function getRole(access: ProjectAccessDto | AccessOptionForTaskDto): UserRole {
  return ("accessRole" in access ? access.accessRole : access.role) as UserRole;
}

function getHighestRole(
  accessList: ProjectAccessDto[] | AccessOptionForTaskDto[],
): UserRole | null {
  let highestRoleValue = -1;
  let highestRole: UserRole | null = null;

  accessList.forEach((access) => {
    const role = getRole(access);
    const roleValue = getRoleLevel(role);
    if (roleValue > highestRoleValue) {
      highestRoleValue = roleValue;
      highestRole = role;
    }
  });

  return highestRole;
}

export const hasRequiredPermissionForAction = (
  accessList: ProjectAccessDto[] | AccessOptionForTaskDto[],
  permission: permissionType,
) => {
  const highestRole = getHighestRole(accessList);
  if (!highestRole) return false;
  return hasRequiredPermission(highestRole, permission);
};

/**
 * Hook to retrieve the current user's role within an organization and group.
 */
export const useRole = (specificTaskId?: string) => {
  const { task: taskFromSelector } = useSelector(
    (state: RootState) => state.tasks,
  );

  const tasks = useSelector((state: RootState) => state.tasks.tasks);

  const task = useMemo(
    () => tasks.find((ta) => ta.id === specificTaskId) || taskFromSelector,
    [specificTaskId, taskFromSelector, tasks],
  );

  const { firstName, lastName } = useSelector(
    (state: RootState) => state.user.userData,
  );
  // Get the members and the current user ID from the Redux state
  const organizationMembers = useSelector(
    (state: RootState) => state.organizations.organization?.organizationMembers,
  );
  const { project } = useSelector((state: RootState) => state.tasks);

  const organizations = useSelector(
    (state: RootState) => state.organizations.organizations,
  );

  const groupMembers = useSelector(
    (state: RootState) => state.organizations.group?.groupMembers,
  );

  const userId = useSelector((state: RootState) => state.user.userData.id);

  const iamMemberInGroup = useCallback(
    (groupId: string) => {
      return organizations.some((org) => {
        return findElementByGroupId(org.groupBranch, groupId);
      });
    },
    [organizations],
  );

  const iamMemberInOrganization = useCallback(
    (orgId: string) => {
      return organizations.some((org) => {
        return org.id === orgId;
      });
    },
    [organizations],
  );

  // Update the organization role whenever organizationMembers or userId changes
  const organizationRole = useMemo(() => {
    if (organizationMembers && userId) {
      const userRole = organizationMembers.find(
        (member) => member.id === userId,
      )?.role;
      return userRole;
    }
  }, [organizationMembers, userId]);

  const memberAccessInProject = useCallback(() => {
    if (project?.ProjectAccess) {
      return project.ProjectAccess.filter((access) => {
        if (access.groupId) {
          return iamMemberInGroup(access.groupId);
        } else if (access.organizationId) {
          return iamMemberInOrganization(access.organizationId);
        } else if (access.userId) {
          return userId === access.userId;
        }
      });
    }
  }, [project?.ProjectAccess]);

  const projectRole = useMemo(() => {
    // The owner keeps every right, like the backend guard does
    if (project && userId && project.ownerId === userId) return "OWNER";
    if (project?.ProjectAccess) {
      const myAccess = memberAccessInProject();
      if (myAccess) return getHighestRole(myAccess);
    } else return null;
  }, [project, userId]);

  const searchForUsersInOrganization = useCallback(
    (searchTerm: string) => {
      if (!organizationMembers) return [];
      const trimmed = searchTerm.trim();
      if (trimmed === "") return organizationMembers;
      const lowerSearch = trimmed.toLowerCase();
      return organizationMembers.filter((member) => {
        const fullName = `${member.firstName} ${member.lastName}`.toLowerCase();
        return (
          member.firstName.toLowerCase().includes(lowerSearch) ||
          member.lastName.toLowerCase().includes(lowerSearch) ||
          member.userName.toLowerCase().includes(lowerSearch) ||
          fullName.includes(lowerSearch)
        );
      });
    },
    [organizationMembers],
  );

  // Like the backend guard: the higher one of the group membership and an
  // organization OWNER/ADMIN role, which carries over into every group (old
  // groups have no OWNER of their own). Group members stay PENDING, as
  // groups have no invitations to accept.
  const groupRole = useMemo(() => {
    if (!userId) return undefined;
    const member = groupMembers?.find(
      (member) =>
        member.id === userId &&
        member.status !== MemberStatus.DECLINED &&
        member.status !== MemberStatus.BLOCKED,
    );
    const inherited =
      organizationRole === "OWNER" || organizationRole === "ADMIN"
        ? organizationRole
        : undefined;
    if (!member?.role) return inherited;
    if (!inherited) return member.role;
    return getRoleLevel(inherited) > getRoleLevel(member.role)
      ? inherited
      : member.role;
  }, [groupMembers, userId, organizationRole]);

  // Check if the user can perform actions within a group
  const canActivateGroup = useCallback(
    (requiredPermission: permissionType, extRole?: UserRole | null) => {
      if (extRole) {
        if (extRole === null) {
          return false;
        }
        return permissionTable[extRole].includes(requiredPermission);
      } else if (groupRole) {
        return permissionTable[groupRole].includes(requiredPermission);
      }
      return false; // Default to false if no role or invalid role
    },
    [groupRole, permissionTable], // Added groupRole and permissionTable as dependencies
  );

  // Check if the user can perform actions within an organization
  const canActivateOrganization = useCallback(
    (requiredPermission: permissionType) => {
      if (organizationRole) {
        return permissionTable[organizationRole].includes(requiredPermission);
      }
      return false; // Default to false if no role or invalid role
    },
    [organizationRole, permissionTable], // Added organizationRole and permissionTable as dependencies
  );

  const canActivateProject = useCallback(
    (requiredPermission: permissionType) => {
      if (projectRole) {
        return permissionTable[projectRole].includes(requiredPermission);
      }
      return false; // Default to false if no role or invalid role
    },
    [organizationRole, permissionTable], // Added organizationRole and permissionTable as dependencies
  );

  const getAccessInfo = useCallback(
    (acc: ProjectAccessDto) => {
      let groupSearchResult: findElementByGroupIdResponse | null = null;
      let orgSearchResult: { name: string; id: string } | undefined;
      let accessParsed: AccessOptionForTaskDto = {
        accessRole: acc.role,
        id: acc.id,
        createdAt: "",
        noteId: acc.projectId,
        accessForUsers: [],
      };

      if (acc.userId) {
        accessParsed.userId = acc.userId;
        accessParsed.user = acc.user;
      } else if (acc.groupId) {
        organizations.some((org) => {
          const result = findElementByGroupId(org.groupBranch, acc.groupId!);
          if (result?.groupBranch) {
            groupSearchResult = result;
            orgSearchResult = { name: org.name, id: org.id };
            return true; // stop searching
          }
          return false;
        });
        accessParsed.group = groupSearchResult
          ? {
              name:
                (groupSearchResult as findElementByGroupIdResponse).groupBranch
                  ?.name ?? "",
              id: acc.groupId,
              organization: {
                name: orgSearchResult?.name ?? "",
                id: orgSearchResult?.id ?? "",
              },
            }
          : undefined;
      } else if (acc.organizationId) {
        const orgForAccess = acc.organizationId
          ? organizations.find((org) => org.id === acc.organizationId)
          : undefined;
        accessParsed.organization = orgForAccess
          ? { name: orgForAccess.name, id: orgForAccess.id }
          : undefined;
      }
      return accessParsed;
    },
    [organizations, firstName, lastName],
  );

  const getRolesForProject = useCallback(
    (project: FullProjectDto): roleInterface[] => {
      if (!project?.ProjectAccess) return [];
      return project.ProjectAccess.reduce<roleInterface[]>((acc, access) => {
        if (access.groupId) {
          organizations.some((org) => {
            const searchResult = findElementByGroupId(
              org.groupBranch,
              access.groupId as string,
            );
            if (searchResult?.groupBranch) {
              acc.push({
                text: searchResult.groupBranch?.name,
                link: `/group/${access.groupId}`,
                type: "group",
                id: access.id,
                trace: org.name + "/" + searchResult.trace,
              });
              return true;
            }
            return false;
          });
        } else if (access.organizationId) {
          const searchResult = organizations.find(
            (org) => org.id === access.organizationId,
          );
          if (searchResult) {
            acc.push({
              text: searchResult.name,
              link: `/organization/${access.organizationId}`,
              type: "organization",
              id: access.id,
            });
          }
        } else if (access.userId && access.user) {
          acc.push({
            text: access.user.firstName + " " + access.user.lastName,
            link: `/user/${access.userId}`,
            type: "user",
            id: access.id,
          });
        }
        return acc;
      }, []);
    },
    [organizations],
  );

  const privateTaskRole: roleInterface | undefined = useMemo(() => {
    if (!task?.NoteAccess) return;

    const privateAcc = task?.NoteAccess.find(
      (acc) => acc.accessRole === "PRIVATE",
    );
    if (!privateAcc) return undefined;

    let role: roleInterface | undefined;

    if (privateAcc.groupId) {
      organizations.forEach((org) => {
        const searchResult = findElementByGroupId(
          org.groupBranch,
          privateAcc.groupId as string,
        );
        if (searchResult?.groupBranch) {
          role = {
            text: searchResult.groupBranch?.name,
            link: `/group/${privateAcc.groupId}`,
            type: "group",
            id: privateAcc.id,
            trace: org.name + "/" + searchResult.trace,
          };
        }
      });
    } else if (privateAcc.organizationId) {
      const searchResult = organizations.find(
        (org) => org.id === privateAcc.organizationId,
      );
      if (searchResult) {
        role = {
          text: searchResult.name,
          link: `/organization/${privateAcc.organizationId}`,
          type: "organization",
          id: privateAcc.id,
        };
      }
    }
    return role;
  }, [task, tasks]);

  return {
    iamMemberInGroup,
    iamMemberInOrganization,
    groupRole,
    organizationRole,
    projectRole,
    privateTaskRole,
    canActivateGroup,
    canActivateOrganization,
    canActivateProject,
    searchForUsersInOrganization,
    getRolesForProject,
    getAccessInfo,
  };
};
